/**
 * Fldev Custom Product Form
 * Gestion du formulaire de personnalisation produit
 */

class FldevCustomProduct {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    
    // Flag pour savoir si l'image actuelle est une image par défaut
    this.isDefaultImage = false;
    
    // Configuration BunnyCDN
    this.BUNNY_CONFIG = {
      storageZoneName: 'val-qrcode-plate',
      accessKey: '57352e87-f6f2-4b83-83638313ec20-03b1-4d5f',
      storageUrl: 'https://storage.bunnycdn.com',
      pullZoneUrl: 'https://qrcode-plate-pull.b-cdn.net',
      folder: 'logos',
    };

    // Données de contenu dynamique
    this.contentData = {
      'avis-google': {
        text: '<b>Partager</b> votre expérience<br> en 10 secondes <b>sur google</b>',
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/google.png',
        backgroundColor: '#4285F4',
        lineColor: '#ABB8FF'
      },
      'follow-instagram': {
        text: 'Suivez-nous sur <b>Instagram</b>',
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/instagram.png',
        backgroundColor: 'linear-gradient(90deg, #4366C9, #B030BB, #FFC551, #F34E55)',
        lineColor: '#ABB8FF'
      },
      'follow-tiktok': {
        text: 'Suivez-nous sur <b>TikTok</b>',
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/tiktok_plate.png',
        backgroundColor: '#000000',
        lineColor: '#26F2EF'
      },
      'follow-facebook': {
        text: 'Suivez-nous sur <b>Facebook</b>',
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/facebook_plate.png',
        backgroundColor: '#1977F3',
        lineColor: '#69AAFF'
      },
      'multi-boost': {
        text: '<b>Partagez</b> votre <b>avis & suivez-nous</b> sur les réseaux sociaux',
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/multi_plate.png',
        backgroundColor: '#506CFB',
        lineColor: '#ABB8FF'
      },
      'avis-tripadvisor': {
        text: "<b>Partagez</b> votre expérience en<br> 10 secondes sur <b>TripAdvisor</b>",
        image: 'https://qrcode-plate-pull.b-cdn.net/logos/assets/trip_plate.png',
        backgroundColor: '#34E0A1',
        lineColor: '#A7FFDF'
      },
    };

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initEditor();
    this.initDefaultText();
    this.attachFormInputs();
    this.validateForm(); // Validation initiale
    this.updatePreview();
  }

  cacheElements() {
    // Éléments du formulaire
    this.typeSelect = this.container.querySelector('#fldev-type-select');
    this.logoUpload = this.container.querySelector('#fldev-logo-upload');
    this.uploadButton = this.container.querySelector('#fldev-upload-button');
    this.imagePreview = this.container.querySelector('#fldev-image-preview');
    this.previewImg = this.container.querySelector('#fldev-preview-img');
    this.removeImageBtn = this.container.querySelector('#fldev-remove-image');
    this.uploadStatus = this.container.querySelector('#fldev-upload-status');

    // Éléments de texte
    this.textEditor = this.container.querySelector('#fldev-text-editor');

    // Éléments de la barre d'outils
    this.btnBold = this.container.querySelector('#fldev-btn-bold');
    this.btnLeft = this.container.querySelector('#fldev-btn-left');
    this.btnCenter = this.container.querySelector('#fldev-btn-center');
    this.btnRight = this.container.querySelector('#fldev-btn-right');

    // Éléments de l'aperçu produit
    this.productPreview = this.container.querySelector('#fldev-product-preview');
    this.overlayLogo = this.container.querySelector('#fldev-overlay-logo');
    this.overlayText = this.container.querySelector('#fldev-overlay-text');
    this.colorInput = this.container.querySelector('#fldev-color-input');
    this.colorPicker = this.container.querySelector('#fldev-color-picker');

    this.lineColorInput = this.container.querySelector('#fldev-line-color-input');
    this.lineColorPicker = this.container.querySelector('#fldev-line-color-picker');
    this.overlayLineColor = this.container.querySelector('#fldev-overlay-line-color');
    this.lineColorHidden = this.container.querySelector('#fldev-line-color-hidden');

    // Inputs cachés
    this.typeHidden = this.container.querySelector('#fldev-type-hidden');
    this.logoUrlHidden = this.container.querySelector('#fldev-logo-url');
    this.textHidden = this.container.querySelector('#fldev-text-hidden');
    this.colorHidden = this.container.querySelector('#fldev-color-hidden');

    // Bouton submit
    this.submitButton = document.querySelector('button[name="add"], input[name="add"]');
  }

  bindEvents() {
    // Changement de type
    this.typeSelect?.addEventListener('change', () => {
      this.handleTypeChange();
      this.validateForm();
    });

    // Upload de logo
    this.uploadButton?.addEventListener('click', () => {
      this.logoUpload?.click(); // Déclenche le clic sur l'input file caché
    });
    this.logoUpload?.addEventListener('change', (e) => {
      this.handleLogoUpload(e);
      this.validateForm();
    });
    this.removeImageBtn?.addEventListener('click', () => {
      this.handleRemoveImage();
      this.validateForm();
    });

    // Gestion des couleurs
    this.colorInput?.addEventListener('input', () => {
      this.handleColorInput();
      this.validateForm();
    });
    this.colorPicker?.addEventListener('input', () => {
      this.handleColorPicker();
      this.validateForm();
    });

    this.lineColorInput?.addEventListener('input', () => {
      this.handleLineColorInput();
      this.validateForm();
    });
    this.lineColorPicker?.addEventListener('input', () => {
      this.handleLineColorPicker();
      this.validateForm();
    });

    // Éditeur de texte
    this.textEditor?.addEventListener('input', () => {
      this.updateTextHidden();
      this.validateForm();
    });
  }

  initEditor() {
    if (!this.options.editableText || !this.btnBold) return;

    // Bouton Gras uniquement
    this.btnBold.addEventListener('click', (e) => {
      e.preventDefault();
      document.execCommand('bold', false, null);
      this.textEditor.focus();
    });

    // Raccourci clavier Ctrl+B
    this.textEditor?.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        document.execCommand('bold', false, null);
      }
    });

    // Forcer la sélection de texte
    this.forceTextSelection();
  }

  forceTextSelection() {
    if (!this.textEditor) return;

    this.textEditor.style.userSelect = 'text';
    this.textEditor.style.webkitUserSelect = 'text';
    this.textEditor.style.mozUserSelect = 'text';
    this.textEditor.style.msUserSelect = 'text';

    // Bloquer les événements qui pourraient empêcher la sélection
    ['mousedown', 'selectstart', 'dblclick'].forEach(eventType => {
      this.textEditor.addEventListener(eventType, (e) => {
        e.stopPropagation();
      });
    });
  }

  initDefaultText() {
    if (!this.textEditor) return;

    // Ajouter le texte par défaut si l'éditeur est vide
    if (!this.textEditor.innerHTML.trim()) {
      this.textEditor.innerHTML = 'Entrez votre texte ici';
      this.textEditor.style.textAlign = 'center';
      this.updateTextHidden();
    }

    // Initialiser la couleur de ligne par défaut
    if (this.lineColorHidden && !this.lineColorHidden.value) {
      this.lineColorHidden.value = '#62a0ea';
      this.lineColorInput.value = '#62a0ea';
      this.lineColorPicker.value = '#62a0ea';
      this.updateLineColorSVG('#62a0ea');
    }
  }

  handleLineColorInput() {
    let value = this.lineColorInput.value.trim();

    if (value && !value.startsWith('#')) {
      value = '#' + value;
      this.lineColorInput.value = value;
    }

    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      this.lineColorHidden.value = value;
      this.lineColorPicker.value = value;
      this.updateLineColorSVG(value);
    }
  }

  handleLineColorPicker() {
    this.lineColorInput.value = this.lineColorPicker.value;
    this.lineColorHidden.value = this.lineColorPicker.value;
    this.updateLineColorSVG(this.lineColorPicker.value);
  }

  updateLineColorSVG(color) {
    if (this.overlayLineColor && color) {
      const path = this.overlayLineColor.querySelector('path');
      if (path) {
        path.setAttribute('stroke', color);
        this.overlayLineColor.style.display = 'block';
      }
    } else if (this.overlayLineColor) {
      this.overlayLineColor.style.display = 'none';
    }
  }

  handleTypeChange() {
    const selectedType = this.typeSelect.value;
    this.typeHidden.value = selectedType;

    if (selectedType && this.contentData[selectedType]) {
      const data = this.contentData[selectedType];
      
      // Appliquer le texte
      this.textEditor.innerHTML = data.text;
      this.textEditor.style.textAlign = 'center';
      this.updateTextHidden();
      
      // Appliquer l'image/logo par défaut SEULEMENT si aucune image uploadée
      if (data.image && !this.isDefaultImage && !this.logoUrlHidden.value) {
        this.logoUrlHidden.value = data.image;
        this.previewImg.src = data.image;
        this.imagePreview.style.display = 'block';
        this.isDefaultImage = true; // Marquer comme image par défaut
        this.removeImageBtn.style.display = 'none'; // Cacher le bouton de suppression
      } else if (data.image && this.isDefaultImage) {
        // Si c'était déjà une image par défaut, on peut la changer
        this.logoUrlHidden.value = data.image;
        this.previewImg.src = data.image;
        this.imagePreview.style.display = 'block';
        this.removeImageBtn.style.display = 'none';
      }
      // Si this.isDefaultImage === false, c'est une image uploadée, on ne la touche pas
      
      // Appliquer la couleur de fond par défaut
      if (data.backgroundColor) {
        this.colorHidden.value = data.backgroundColor;
        this.colorInput.value = data.backgroundColor;
        this.colorPicker.value = data.backgroundColor;
      }
      
      // Appliquer la couleur de ligne par défaut
      if (data.lineColor) {
        this.lineColorHidden.value = data.lineColor;
        this.lineColorInput.value = data.lineColor;
        this.lineColorPicker.value = data.lineColor;
        this.updateLineColorSVG(data.lineColor);
      }
    } else {
      // Vider tous les champs si aucun type sélectionné
      this.textEditor.innerHTML = '';
      this.textHidden.value = '';
      this.logoUrlHidden.value = '';
      this.imagePreview.style.display = 'none';
      this.previewImg.src = '';
      this.isDefaultImage = false; // Pas d'image par défaut
      this.removeImageBtn.style.display = 'none'; // Cacher le bouton
    }
    
    this.updatePreview();
  }

  async handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.showUploadStatus('❌ Veuillez sélectionner une image valide', 'error');
      return;
    }

    // Générer le timestamp UNE SEULE FOIS
    const timestamp = Date.now();

    // Aperçu local
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImg.src = e.target.result;
      this.imagePreview.style.display = 'block';
      this.logoUrlHidden.value = e.target.result;
      this.isDefaultImage = false; // Marquer comme image uploadée
      this.removeImageBtn.style.display = 'block'; // Montrer le bouton de suppression
      this.updatePreview();
    };
    reader.readAsDataURL(file);

    // Upload vers BunnyCDN
    this.showUploadStatus('⏳ Upload en cours...', 'loading');

    try {
      const uploadedUrl = await this.uploadToBunny(file, timestamp);
      this.logoUrlHidden.value = uploadedUrl;
      this.updatePreview();
      this.showUploadStatus('✅ Image uploadée avec succès', 'success');
    } catch (error) {
      this.showUploadStatus(`❌ Erreur lors de l'upload: ${error.message}`, 'error');
      console.error('Upload error:', error);
    }
  }

  async handleRemoveImage() {
    if (this.logoUrlHidden.value) {
      this.showUploadStatus('⏳ Suppression en cours...', 'loading');

      try {
        await this.deleteFromBunny(this.logoUrlHidden.value);
        this.showUploadStatus('✅ Image supprimée', 'success');

        setTimeout(() => {
          this.uploadStatus.textContent = '';
        }, 2000);
      } catch (error) {
        this.showUploadStatus('⚠️ Image retirée (erreur de suppression côté serveur)', 'warning');
        console.error('Delete error:', error);
      }
    }

    this.logoUpload.value = '';
    this.imagePreview.style.display = 'none';
    this.previewImg.src = '';
    this.logoUrlHidden.value = '';
    this.updatePreview();
  }

  handleColorInput() {
    let value = this.colorInput.value.trim();

    if (value && !value.startsWith('#')) {
      value = '#' + value;
      this.colorInput.value = value;
    }

    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      this.colorHidden.value = value;
      this.colorPicker.value = value;
      this.updatePreview();
    }
  }

  handleColorPicker() {
    this.colorInput.value = this.colorPicker.value;
    this.colorHidden.value = this.colorPicker.value;
    this.updatePreview();
  }

  updateTextHidden() {
    // Stocker directement le HTML (avec le gras)
    this.textHidden.value = this.textEditor.innerHTML;
    this.updatePreview();
  }

  updatePreview() {
    // Mettre à jour le logo
    if (this.logoUrlHidden.value) {
      this.overlayLogo.src = this.logoUrlHidden.value;
      this.overlayLogo.style.display = 'block';
    } else {
      this.overlayLogo.style.display = 'none';
    }

    // Mettre à jour le texte avec formatage
    if (this.textHidden.value) {
      this.overlayText.innerHTML = this.textHidden.value;
      this.overlayText.style.display = 'block';
    } else {
      this.overlayText.style.display = 'none';
    }

    // Mettre à jour la couleur de ligne SVG
    if (this.lineColorHidden.value) {
      this.updateLineColorSVG(this.lineColorHidden.value);
    } else {
      this.overlayLineColor.style.display = 'none';
    }

    // Mettre à jour la couleur de fond
    if (this.colorHidden.value) {
      this.productPreview.style.background = this.colorHidden.value;
    }
  }

  async uploadToBunny(file, timestamp) {
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${sanitizedName}`;
    const filePath = `${this.BUNNY_CONFIG.folder}/${fileName}`;
    const uploadUrl = `${this.BUNNY_CONFIG.storageUrl}/${this.BUNNY_CONFIG.storageZoneName}/${filePath}`;

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        AccessKey: this.BUNNY_CONFIG.accessKey,
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    return `${this.BUNNY_CONFIG.pullZoneUrl}/${filePath}`;
  }

  async deleteFromBunny(fileUrl) {
    const urlParts = fileUrl.replace(this.BUNNY_CONFIG.pullZoneUrl + '/', '');
    const deleteUrl = `${this.BUNNY_CONFIG.storageUrl}/${this.BUNNY_CONFIG.storageZoneName}/${urlParts}`;

    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        AccessKey: this.BUNNY_CONFIG.accessKey,
      },
    });

    if (!response.ok) {
      throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
    }

    return true;
  }

  showUploadStatus(message, type) {
    this.uploadStatus.textContent = message;
    
    const colors = {
      loading: '#007bff',
      success: 'green',
      error: 'red',
      warning: 'orange'
    };
    
    this.uploadStatus.style.color = colors[type] || '#000';
  }

  attachFormInputs() {
    const hiddenInputs = [
      this.typeHidden,
      this.logoUrlHidden,
      this.textHidden,
      this.colorHidden,
      this.lineColorHidden
    ];

    // Pour le mode 'id', vérifier si les inputs ont déjà un attribut form valide
    if (this.options.formMode === 'id') {
      const formAttribute = this.typeHidden?.getAttribute('form');
      if (formAttribute) {
        const targetForm = document.getElementById(formAttribute);
        if (targetForm) {
          console.log('Mode ID: Formulaire trouvé avec ID:', formAttribute);
          return; // Les inputs sont déjà correctement attachés
        }
      }
    }

    // Chercher le formulaire produit principal
    let productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    
    if (!productForm) {
      productForm = document.querySelector('form.form');
    }

    if (productForm) {
      console.log('Formulaire produit trouvé:', productForm);
      const formId = productForm.getAttribute('id');
      
      if (formId) {
        // Attacher les inputs via l'attribut form
        hiddenInputs.forEach((input) => {
          if (input) {
            input.setAttribute('form', formId);
            console.log('Input attaché au formulaire:', input.name, 'form ID:', formId);
          }
        });
      } else {
        // Insérer directement dans le formulaire
        hiddenInputs.forEach((input) => {
          if (input) {
            const clonedInput = input.cloneNode(true);
            clonedInput.removeAttribute('form'); // Retirer l'attribut form car on l'insère directement
            input.style.display = 'none';
            productForm.appendChild(clonedInput);
            console.log('Input cloné et inséré dans le formulaire:', clonedInput.name);
          }
        });
      }
    } else {
      console.error('Aucun formulaire produit trouvé!');
    }
  }

  validateForm() {
    if (!this.submitButton) return;

    // Vérifier si tous les champs obligatoires sont remplis
    const isTypeSelected = this.typeSelect?.value && this.typeSelect.value !== '';
    const hasLogo = this.logoUrlHidden?.value && this.logoUrlHidden.value !== '';
    const hasText = this.textHidden?.value && this.textHidden.value !== '' && this.textHidden.value !== 'Entrez votre texte ici';
    const hasLineColor = this.lineColorHidden?.value && this.lineColorHidden.value !== '';
    const hasColor = this.colorHidden?.value && this.colorHidden.value !== '';

    // Le formulaire est valide si tous les champs sont remplis
    const isFormValid = isTypeSelected && hasLogo && hasText && hasLineColor && hasColor;

    // Activer/désactiver le bouton
    // this.submitButton.disabled = !isFormValid;
  }
}

// Initialisation automatique
document.addEventListener('DOMContentLoaded', function() {
  const containers = document.querySelectorAll('.fldev-custom-form-container');
  
  containers.forEach(container => {
    const formMode = container.dataset.formMode || 'ajax';
    const editableText = container.dataset.editableText === 'true';
    
    new FldevCustomProduct(container, {
      formMode: formMode,
      editableText: editableText
    });
  });
});